import {
  faCheck,
  faChevronDown,
  faChevronRight,
  faLock,
  faReply,
  faStar,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Text } from '@wfp/react';
import { Tooltip } from 'acr_ui/sources/components/Tooltip';
import { formatDistanceToNow } from 'date-fns';
import isEmpty from 'lodash/isEmpty';
import React, { useEffect, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { Element } from 'react-scroll';

import ConfirmationModal from 'src/components/ConfirmationModal';
import { resolveCrf } from 'src/crf';
import { useIsMount } from 'src/hooks';
import {
  commentReplyRequestAPI,
  commentRequestAPI,
  commentRestoreRequestAPI,
} from 'src/redux/comments/actions';
import {
  COMMENTS_LIST,
  REPLY,
  REPLY_DETAIL,
  REPORT_DETAIL_DATA_KEY,
  SECTION_DETAIL_DATA_KEY,
} from 'src/redux/constants';
import type { RootState } from 'src/redux/store';
import type { IAppConfig, ICommentPermissions } from 'src/types';
import {
  anchorsArrayFromNarrative,
  getDateWithTimezone,
  getUserInfo,
  sendGAEvent,
} from 'src/utils';
import CommentsTextInput from './commentsTextInput';
import ReviewIcon from './ReviewIcon';

interface CommentListItemProps {
  data?: any;
  commentsContainerID?: string | null;
  sectionAPIKwargs?: any;
  props?: any;
  id?: string | number;
  nested?: boolean;
  parentCommentID?: string | number;
  issuesOverview?: boolean;
  activeUser?: string;
  permissions?: ICommentPermissions;
  parentClosed?: boolean;
  highlightIsHovered?: boolean;
  highlightComment?: string;
  expandAll?: boolean;
}

export const CommentListItemComponent = ({
  data,
  commentsContainerID,
  sectionAPIKwargs,
  props,
  id,
  nested = false,
  parentCommentID,
  issuesOverview = false,
  activeUser,
  permissions,
  parentClosed,
  highlightIsHovered,
  highlightComment,
  expandAll = true,
}: CommentListItemProps) => {
  const dispatch = useDispatch();

  const [collapsed, setCollapsed] = useState(data?.closed || false);
  const [showReplyForm, setShowReplyForm] = useState(false);

  useEffect(() => {
    if (!nested && !isMount) {
      setCollapsed(!expandAll);
    }
  }, [expandAll, nested]);
  const [replyCommentContent, setReplyCommentContent] = useState('');
  const [newContent, setNewContent] = useState(data.content || '');
  const [highlightAnchor, setHighlightAnchor] = useState(false);
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] =
    useState(false);
  const [showCloseConfirmationModal, setCloseConfirmationModal] =
    useState(false);
  const [showRestoreConfirmationModal, setRestoreConfirmationModal] =
    useState(false);
  const [edit, setEdit] = useState(false);
  const [newTitle, setNewTitle] = useState(data.title || '');

  const appConfig = useSelector(
    (state: RootState): IAppConfig | undefined =>
      state.viewData?.GET_APP_CONFIGURATION?.data,
  );
  const roles = appConfig?.userInfo?.roles;
  const isHqAdmin = roles && roles.includes('hq_admin');

  const isMount = useIsMount();

  useEffect(() => {
    if (!nested && highlightComment && data?.comments && data?.closed) {
      const hasHighlightedReply = data.comments.some(
        reply => reply.pk.toString() === highlightComment,
      );

      if (hasHighlightedReply && collapsed) {
        setTimeout(() => {
          setCollapsed(false);
        }, 50);
      }
    }
  }, [highlightComment, data?.comments, nested, data?.closed, collapsed]);

  useEffect(() => {
    updateAnchorStyle();
    return () => {
      const elements = document.querySelectorAll(`[id='${data.anchor}']`);
      elements.forEach(element => {
        if (!element.classList.contains('comment-anchor')) return;
        (element as HTMLElement).style.removeProperty('background-color');
      });
    };
  }, [highlightAnchor]);

  function toggleEdit() {
    setEdit(!edit);
  }

  function toggleDeleteConfirmationModal() {
    setShowDeleteConfirmationModal(!showDeleteConfirmationModal);
  }

  function toggleCloseConfirmationModal() {
    setCloseConfirmationModal(!showCloseConfirmationModal);
  }

  function toggleRestoreConfirmationModal() {
    setRestoreConfirmationModal(!showRestoreConfirmationModal);
  }

  function toggleCollapsed() {
    setCollapsed(!collapsed);
  }

  function updateAnchorStyle() {
    const elements = document.querySelectorAll(`[id='${data.anchor}']`);
    elements.forEach(element => {
      if (!element.classList.contains('comment-anchor')) return;

      const el = element as HTMLElement;
      if (highlightAnchor) {
        el.style.setProperty('background-color', '#e03e04', 'important');
      } else {
        el.style.removeProperty('background-color');
      }
    });
  }

  function updateComment(
    requestData: any,
    method: string,
    refreshNarrative = false,
  ) {
    let refreshNarrativeType = '';

    if (refreshNarrative) {
      const narrativeArray = anchorsArrayFromNarrative(props.data.content);
      const isLinkedMainNarrative = narrativeArray.includes(data.anchor);

      let isLinkedExtraNarrative = false;
      if (!isLinkedMainNarrative) {
        props.data.extra_narratives.forEach(extraNarrative => {
          const extraNarrativeArray = anchorsArrayFromNarrative(
            extraNarrative.content,
          );

          if (extraNarrativeArray.includes(data.anchor))
            isLinkedExtraNarrative = true;
        });
      }

      refreshNarrativeType = props.crf.narrativeRefreshType({
        isLinkedMainNarrative,
        isLinkedExtraNarrative,
      });
    }

    const params: any = {
      url_format: {
        ...sectionAPIKwargs,
        _comment_: data.pk,
      },
      method,
      data: requestData,
      storage_key: COMMENTS_LIST,
      reportId: props.reportId,
      refreshNarrative: refreshNarrativeType,
    };

    dispatch(commentRequestAPI(params));
  }

  function createReply() {
    setReplyCommentContent('');

    const params = {
      url_format: {
        ...sectionAPIKwargs,
        _comment_: data.pk,
      },
      method: 'post',
      data: { content: replyCommentContent },
      storage_key: REPLY,
    };

    dispatch(commentReplyRequestAPI(params));
  }

  function updateExistingComment() {
    const requestData: any = { content: newContent };
    if (!nested) {
      requestData['title'] = newTitle;
      requestData['edit'] = true;
      updateComment(requestData, 'patch');
      toggleEdit();
    } else {
      toggleEdit();
      const params = {
        url_format: {
          ...sectionAPIKwargs,
          _comment_: parentCommentID,
          _reply_: data.pk,
        },
        method: 'patch',
        data: requestData,
        storage_key: REPLY_DETAIL,
      };
      dispatch(commentReplyRequestAPI(params));
    }
  }

  function deleteCommentReply() {
    toggleDeleteConfirmationModal();

    const params = {
      url_format: {
        ...sectionAPIKwargs,
        _comment_: parentCommentID,
        _reply_: data.pk,
      },
      method: 'delete',
      data: {},
      storage_key: REPLY_DETAIL,
    };

    dispatch(commentReplyRequestAPI(params));
  }

  function deleteComment() {
    setHighlightAnchor(false);
    updateComment({}, 'delete', true);
  }

  function markAsClosed() {
    setHighlightAnchor(false);
    updateComment({ closed: true }, 'patch', true);
    toggleCloseConfirmationModal();
  }

  function restoreComment() {
    dispatch(
      commentRestoreRequestAPI({
        url_format: {
          ...sectionAPIKwargs,
          _comment_: parentCommentID,
          _reply_: data.pk,
        },
        data: {
          comment_id: id as number,
        },
      }),
    );
    toggleRestoreConfirmationModal();
  }

  function toggleAsImportant() {
    updateComment({ important: !data?.important }, 'patch');
  }

  function updateReplyContent(e: React.ChangeEvent<HTMLTextAreaElement>) {
    setReplyCommentContent(e.target.value);
  }

  function updateNewContent(e: React.ChangeEvent<HTMLTextAreaElement>) {
    setNewContent(e.target.value);
  }

  function updateNewTitle(e: React.ChangeEvent<HTMLInputElement>) {
    setNewTitle(e.target.value);
  }

  function getDeleteCommentHeading() {
    const closingText = 'Do you really want to proceed?';
    const headingComment = `You are about to delete comment with ID ${data.pk}${
      data.comments?.length > 0 ? ' and all its replies.' : '.'
    } \n${closingText}`;
    const headingIssue = `You are about to delete reply with ID ${data.pk}. \n${closingText}`;

    return nested ? headingIssue : headingComment;
  }

  const nested_comments = data?.comments?.map((comment_data: any) => {
    const id = comment_data.pk;
    const isHighlighted =
      highlightComment && comment_data.pk.toString() === highlightComment;

    const props = {
      data: comment_data,
      id,
      parentClosed: data?.closed,
      parentCommentID: data?.pk,
      activeUser: activeUser,
      sectionAPIKwargs: !issuesOverview ? sectionAPIKwargs : null,
      commentsContainerID: !issuesOverview ? commentsContainerID : null,
      permissions: !issuesOverview ? permissions : {},
      nested: true,
      issuesOverview,
      highlightIsHovered: isHighlighted,
      highlightComment,
      expandAll,
    };
    return <CommentListItem key={id} {...props} />;
  });

  const deleteMethod = !nested ? deleteComment : deleteCommentReply;

  const elementId = data.anchor || `comment-${data.pk}`;

  return (
    <Element
      className={`${
        nested
          ? 'nested-comment'
          : data?.closed
            ? 'closed-comment'
            : data?.important
              ? 'important-comment'
              : ''
      }
          ${highlightIsHovered ? 'highlighted' : ''}
          acr-comment`}
      onMouseEnter={() => setHighlightAnchor(true)}
      onMouseLeave={() => setHighlightAnchor(false)}
      name={elementId}
      id={elementId}
      data-comment-id={data.pk}
      data-test-id="comment-item"
    >
      {!nested && (
        <div className="acr-comment-header-bar" onClick={toggleCollapsed}>
          <FontAwesomeIcon
            icon={collapsed ? faChevronRight : faChevronDown}
            className="acr-comment-chevron"
            width="16"
            height="16"
          />
          {data.title && (
            <span className="acr-comment-header-title">{data.title}</span>
          )}
          {collapsed && data?.important && !data?.closed && (
            <FontAwesomeIcon
              icon={faStar}
              className="acr-comment-header-bar-star"
              width="14"
              height="14"
            />
          )}
          {collapsed && data?.closed && (
            <FontAwesomeIcon
              icon={faLock}
              className="acr-comment-header-bar-lock"
              width="14"
              height="14"
            />
          )}
          <span className="acr-comment-reply-counter">
            {data?.comments?.length || 0}{' '}
            {(data?.comments?.length || 0) === 1 ? 'reply' : 'replies'}
          </span>
        </div>
      )}
      {!nested && !collapsed && (
        <div className="comment-cover">
          <div className="acr-comment-header">
            <div className="acr-comment-title" data-test-id="comment-author">
              <Text className="acr-comment-username">
                {getUserInfo(data.user)}
              </Text>
            </div>
          </div>
          <div className="comment-header-actions">
            {data?.restored && (
              <div
                className="thread-restored-label"
                data-test-id="restored-label"
              >
                <span>Restored</span>
              </div>
            )}

            {!issuesOverview &&
              !props.reportIsPublished &&
              !data?.closed &&
              permissions.add_comment && (
                <Tooltip
                  title={showReplyForm ? 'Cancel reply' : 'Reply'}
                  trigger="mouseenter"
                  theme="light"
                  position="top"
                >
                  <FontAwesomeIcon
                    icon={faReply}
                    className="comment-action-icon"
                    style={showReplyForm ? { color: '#007dbc' } : undefined}
                    width="20"
                    height="20"
                    onClick={() => {
                      if (showReplyForm) setReplyCommentContent('');
                      setShowReplyForm(prev => !prev);
                    }}
                    data-test-id="reply-icon"
                  />
                </Tooltip>
              )}

            {!issuesOverview && !props.reportIsPublished && (
              <>
                {isHqAdmin && data?.closed ? (
                  <Tooltip
                    title="Restore comment"
                    trigger="mouseenter"
                    theme="light"
                    position="top"
                  >
                    <div
                      className="closed-comment-badge"
                      onClick={() => {
                        if (!props.isCommentsUpdating) {
                          toggleRestoreConfirmationModal();
                        }
                      }}
                      data-test-id="restore-icon"
                    >
                      <FontAwesomeIcon icon={faLock} width="14" height="14" />
                      <span>Closed</span>
                    </div>
                  </Tooltip>
                ) : (
                  !data?.closed &&
                  (permissions.mark_closed_issue ||
                    (permissions.mark_closed_own_issue &&
                      data?.user?.username === activeUser)) && (
                    <Tooltip
                      title="Mark as closed"
                      trigger="mouseenter"
                      theme="light"
                      position="top"
                    >
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="comment-action-icon"
                        width="20"
                        height="20"
                        onClick={() => {
                          if (!props.isCommentsUpdating) {
                            toggleCloseConfirmationModal();
                          }
                        }}
                        data-test-id="close-icon"
                      />
                    </Tooltip>
                  )
                )}
              </>
            )}

            {!issuesOverview &&
              !props.reportIsPublished &&
              !data?.closed &&
              permissions.mark_important_issue && (
                <>
                  {data?.important ? (
                    <Tooltip
                      title="Remove high importance"
                      trigger="mouseenter"
                      theme="light"
                      position="top"
                    >
                      <div
                        className="high-importance-badge"
                        data-test-id="high-importance"
                        onClick={() => {
                          if (!props.isCommentsUpdating) {
                            toggleAsImportant();
                          }
                        }}
                      >
                        <FontAwesomeIcon icon={faStar} width="14" height="14" />
                        <span>High Importance</span>
                      </div>
                    </Tooltip>
                  ) : (
                    <Tooltip
                      title="Mark as high importance"
                      trigger="mouseenter"
                      theme="light"
                      position="top"
                    >
                      <FontAwesomeIcon
                        icon={faStar}
                        className="high-importance-icon"
                        width="20"
                        height="20"
                        onClick={() => {
                          if (!props.isCommentsUpdating) {
                            toggleAsImportant();
                          }
                        }}
                      />
                    </Tooltip>
                  )}
                </>
              )}

            {!issuesOverview && !isEmpty(permissions) && (
              <ReviewIcon
                permissions={permissions}
                closed={data?.closed}
                user={data?.user}
                activeUser={activeUser}
                data={data}
                toggleDeleteConfirmationModal={toggleDeleteConfirmationModal}
                toggleEdit={toggleEdit}
                isReportPublished={props.reportIsPublished}
                nested={nested}
                parentClosed={parentClosed}
                isCommentsUpdating={props.isCommentsUpdating}
              />
            )}
          </div>
        </div>
      )}

      {!collapsed && (
        <div className={`comment-body${nested ? ' comment-body-nested' : ''}`}>
          {nested && (
            <div className="comment-info">
              <div className="nested-reply-header-wrapper">
                <FontAwesomeIcon
                  icon={faReply}
                  width="16"
                  height="16"
                  className="nested-comment-reply-header reply-icon"
                />
                <Text className="nested-comment-reply-header">
                  {getUserInfo(data.user)}
                </Text>
              </div>
              {!issuesOverview && !isEmpty(permissions) && (
                <ReviewIcon
                  permissions={permissions}
                  closed={data?.closed}
                  user={data?.user}
                  activeUser={activeUser}
                  data={data}
                  toggleDeleteConfirmationModal={toggleDeleteConfirmationModal}
                  toggleEdit={toggleEdit}
                  isReportPublished={props.reportIsPublished}
                  nested={nested}
                  parentClosed={parentClosed}
                  isCommentsUpdating={props.isCommentsUpdating}
                />
              )}
            </div>
          )}
          {!nested && (
            <div className="comment-info">
              <Text className="acr-comment-title-text">{data.title}</Text>
            </div>
          )}
          {nested && data.title && (
            <div className="comment-info">
              <Text className="acr-comment-title-text">{data.title}</Text>
            </div>
          )}

          {!issuesOverview && (
            <>
              {showDeleteConfirmationModal && (
                <ConfirmationModal
                  open={true}
                  onSubmit={() => {
                    sendGAEvent('comment_delete_confirmation_btn_clicked');
                    deleteMethod();
                  }}
                  onClose={toggleDeleteConfirmationModal}
                  heading={getDeleteCommentHeading()}
                  primaryButtonText="Yes"
                  secondaryButtonText="No"
                  customClassName="delete-comment-modal"
                />
              )}

              {showCloseConfirmationModal && (
                <ConfirmationModal
                  open={true}
                  onSubmit={markAsClosed}
                  onClose={toggleCloseConfirmationModal}
                  heading={`Do you really want to mark comment with ID ${data.pk} as closed?`}
                  primaryButtonText="Yes"
                  secondaryButtonText="No"
                  customClassName="close-comment-modal"
                />
              )}

              {showRestoreConfirmationModal && (
                <ConfirmationModal
                  open={true}
                  onSubmit={restoreComment}
                  onClose={toggleRestoreConfirmationModal}
                  heading={`Do you really want to restore comment with ID ${data.pk}?`}
                  primaryButtonText="Yes"
                  secondaryButtonText="No"
                  customClassName="restore-comment-modal"
                />
              )}
            </>
          )}

          <Text className="comment-content">{data.content}</Text>
          <div className="comment-id-date-wrapper">
            <Text className="comment-body__pk-part">ID: {data.pk}</Text>
            <Text className="comment-date">
              {getDateWithTimezone(data.date)}
              <span className="comment-date-separator"> • </span>
              {`${formatDistanceToNow(data.date.date)} ago`}
            </Text>
          </div>
          {!nested &&
            !data?.closed &&
            !issuesOverview &&
            !props.reportIsPublished &&
            showReplyForm && (
              <CommentsTextInput
                displayTitleInput={false}
                contentValue={replyCommentContent}
                handleChange={updateReplyContent}
                submitButtonLabel="Post Reply"
                handleSubmit={() => {
                  sendGAEvent('comment_post_reply_btn_clicked');
                  createReply();
                  setShowReplyForm(false);
                }}
                handleCancel={() => {
                  setShowReplyForm(false);
                  setReplyCommentContent('');
                }}
                isNestedForm={true}
                placeholder="Reply here ..."
              />
            )}

          {edit && !issuesOverview && (
            <CommentsTextInput
              displayTitleInput={!nested}
              contentValue={newContent}
              titleValue={newTitle}
              handleChange={updateNewContent}
              handleChangeTitle={updateNewTitle}
              submitButtonLabel={`Update ${nested ? 'Reply' : 'Comment'}`}
              handleSubmit={updateExistingComment}
              handleCancel={toggleEdit}
              isNestedForm={nested}
              placeholder={`${nested ? 'Reply' : 'Comment'} here ...`}
            />
          )}
          {nested_comments}
        </div>
      )}
    </Element>
  );
};

export const mapStateToProps = (state: RootState) => {
  return {
    props: {
      activeSectionID: 0,
      isCommentsUpdating: state.viewData[COMMENTS_LIST]?.isFetching || false,
      ...state.viewData[SECTION_DETAIL_DATA_KEY],
      ...state.viewState,
      crf: resolveCrf(state.viewData[REPORT_DETAIL_DATA_KEY]?.data?.crf),
      reportId: state.viewData[REPORT_DETAIL_DATA_KEY]?.data?.pk,
      reportIsPublished:
        state.viewData[REPORT_DETAIL_DATA_KEY]?.data?.state === 'Published',
    },
  };
};

const CommentListItem = connect(mapStateToProps)(
  CommentListItemComponent,
) as React.ComponentType<any>;
export default CommentListItem;
